import { useNavigate } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { Base, LockIcon, ReverLoading } from '@itsrever/design-system'
import { LoadError, PageHeader } from '@/ui/shared'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { useGroupWarehouses } from '@/application/manage-warehouses'
import { Warehouse } from '@/domain/warehouses'
import { RAW_TEXT } from './hub-parts'

const { Button, Callout, Card } = Base

// Read-only here: addresses and routing are edited in Configuration > Warehouses.
export const WAREHOUSES_CONFIG_PATH = '/configuration/warehouses'

const addressOf = (w: Warehouse) =>
  [w.addressLine1, [w.postcode, w.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

const contactName = (w: Warehouse) =>
  [w.firstName, w.lastName].filter(Boolean).join(' ')

export default function WarehousesSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scope } = useWarehousesScope()
  const group = scope.kind === 'none' ? undefined : scope.group
  const { warehouses, loadingWarehouses, warehousesError, reloadWarehouses } =
    useGroupWarehouses(group)

  if (!group) {
    return (
      <>
        <PageHeader title={t('integrations_hub.warehouses.title')} />
        <p className="px-7 py-6 text-sm text-neutral-600">
          {t('integrations_hub.no_scope')}
        </p>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('integrations_hub.warehouses.title')}
        description={t('integrations_hub.warehouses.group_description', {
          group,
          ...RAW_TEXT
        })}
      />
      <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
        <Callout
          tone="info"
          icon={<LockIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="integrations_hub.warehouses.banner"
            components={{
              b: <b />,
              action: (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  inline
                  onClick={() => navigate(WAREHOUSES_CONFIG_PATH)}
                />
              )
            }}
          />
        </Callout>

        {warehousesError ? (
          <LoadError
            message={t('integrations_hub.warehouses.load_error')}
            onRetry={() => reloadWarehouses()}
          />
        ) : loadingWarehouses ? (
          <ReverLoading />
        ) : warehouses.length === 0 ? (
          <Card variant="flat">
            <p className="px-4 py-6 text-sm text-neutral-600">
              {t('integrations_hub.warehouses.empty')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
            {warehouses.map((w) => (
              <Card key={w.id} className="flex flex-col gap-1 p-4">
                <p className="text-sm font-medium text-neutral-900">
                  {w.company}
                </p>
                <p className="text-xs text-neutral-600">{addressOf(w)}</p>
                <p className="text-xs text-neutral-600">
                  {w.country || w.countryCode}
                </p>
                {contactName(w) ? (
                  <p className="text-xs text-neutral-600">{contactName(w)}</p>
                ) : null}
                {w.email ? (
                  <p className="text-xs text-neutral-600">{w.email}</p>
                ) : null}
                <p className="text-xs text-neutral-400">
                  {t('integrations_hub.warehouses.id', {
                    id: w.id,
                    ...RAW_TEXT
                  })}
                </p>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
